import { Component, OnDestroy, OnInit } from '@angular/core';
import Highcharts from 'highcharts';
import { IUser } from 'projects/common/src/app/interfaces';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { firstLetterUpper } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DateTimePresentationPipe } from 'src/app/modules/core/pipes/date-time-presentation.pipe';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { IStoredProcedureInputs } from '../../../interfaces/chart-widget';
import { IGeneralDrillData, IGeneralDrillSeries } from '../../../interfaces/sp/general-drill';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-general-drilling',
  templateUrl: './general-drilling.component.html',
  styleUrls: ['./general-drilling.component.scss'],
})
export class GeneralDrillingComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public hasData = false;
  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private formatDatePipe: InputDateFormatPipe,
    private languageService: LanguageService,
    protected screenViewService: ScreenViewService,
    private datePipe: DateTimePresentationPipe,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Build request params.
   */
  protected buildRequestParams(): IStoredProcedureInputs {
    const companyId = String((this.parameterService.parameters.item('user-data').value as IUser).company.id);
    const inputDate = String(this.parameterService.parameters.item('date').value);
    const shiftId = Number((this.parameterService.parameters.item('shift').value as IShift).shiftId);
    const { zoneId } = this.cacheService.session('selectedZone');

    return {
      inputDate,
      shiftId,
      companyId,
      zoneId,
    };
  }

  /**
   * Draw chart in target
   */
  public drawView(chartOptions?: IGeneralDrillData): void {
    this.hasData = !!chartOptions;
    if (chartOptions.drillClasification.length > 0 && chartOptions.machineDrillsByClassification.length > 0) {
      const { machineDrillsByClassification } = chartOptions;
      this.hasData = !!machineDrillsByClassification.length;

      const machineNames = machineDrillsByClassification.map((machine) => machine.machineName);

      const date = this.formatDatePipe.transform(this.parameterService.parameters.item('date').value);
      const drillingText = this.languageService.message('drilling');
      const xPrefix = 'mts';

      const equipmentsText = firstLetterUpper(this.languageService.message('equipments'));
      const metersText = firstLetterUpper(this.languageService.message('meters'));

      const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
      const shiftValue = this.parameterService.parameters.item('shift').value;
      const shiftName = shiftValue.name;
      const user = this.parameterService.parameters.item('user-data').value;

      const series = this.buildSeries(chartOptions);
      const height = 550 + series.length * 30;

      if (this.hasData) {
        const configChart = {
          chart: {
            type: 'bar',
            plotBorderWidth: null,
            plotShadow: false,
            backgroundColor: '#F8F8F8',
            style: {
              fontFamily: 'Open Sans',
            },
            height,
          },
          credits: {
            enabled: false,
          },
          title: {
            text: '',
          },
          subtitle: {
            text: `${this.languageService.message('company')}: ${user.company.name},
                ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
                ${this.languageService.message('shift_')}: ${shiftName}`,
          },
          xAxis: {
            categories: machineNames,
            title: {
              text: equipmentsText.toUpperCase(),
              style: {
                fontSize: '12px',
                color: '#707070',
                fontWeight: '600',
              },
            },
            gridLineWidth: 1,
            tickInterval: 1,
          },
          yAxis: {
            min: 0,
            title: {
              text: metersText.toUpperCase(),
              style: {
                fontSize: '12px',
                color: '#707070',
                fontWeight: '600',
              },
            },
            labels: {
              format: `{value} ${xPrefix}`,
            },
            plotLines: [
              {
                color: '#707070',
                width: 2,
                value: 0,
              },
            ],
            stackLabels: {
              style: {
                color: 'black',
              },
              enabled: true,
            },
          },
          tooltip: {
            backgroundColor: 'rgb(51, 51, 51, 1)',
            borderColor: '#333333',
            borderRadius: 2,
            style: {
              color: '#FFFFFF',
            },
            useHTML: true,
            formatter(): string {
              const drillType = `${drillingText.toUpperCase()} ${this.series.name.toUpperCase()}`;
              let divSymbol = '';
              divSymbol = `<div style="background-color: ${this.color};
                            width: 30px;
                            height: 12px;
                            border: 1px solid ${this.color};
                            float: left;
                            margin-top: 2px">
                        </div>`;
              return `<div style="width: 180px;
                                font-size: 10px;
                                color: #FFFFFF;">
                      <strong style="font-weight: 700;">${drillType}</strong> <br>
                      <span style="font-weight: 400;">${this.x}</span> <br>
                      ${divSymbol} &nbsp;
                      <span style="font-weight: 400;">${drillingText}: ${this.y} ${xPrefix}</span>
                    </div>`;
            },
          },
          legend: {
            layout: 'horizontal',
            squareSymbol: false,
            align: 'center',
            verticalAlign: 'top',
            margin: 30,
            useHTML: true,
            labelFormatter(): string {
              return `<div style="height: 20px; display: flex;">
                      <svg width="36" height="18" style="float: left;">
                        <rect width="30px" height="12px" x="2" y="2"
                        style="fill: ${this.color}; stroke-width: 2px !important;
                        stroke: ${this.userOptions.borderColor} !important;"/>
                      </svg>
                      <span style="font-size: 12px; font-family: Open Sans; font-weight: 400;
                      color: #433E3E; float: left; margin-left: 7px">${this.name}</span>
                    </div>`;
            },
            symbolPadding: 0.001,
            symbolWidth: 0.001,
            symbolRadius: 0.001,
            itemStyle: {
              cursor: 'default',
            },
          },
          plotOptions: {
            series: {
              stacking: 'normal',
              events: {
                legendItemClick(): boolean {
                  return false;
                },
              },
            },
            bar: {
              dataLabels: {
                enabled: true,
                formatter(): number | string {
                  return this.y !== 0 ? this.y : '';
                },
              },
            },
          },
          series,
        };

        this.chart = Highcharts.chart(this.chartTarget.nativeElement, configChart as any, (chart) => {
          if (!this.hasData) {
            const newX = chart.plotWidth / 3 + chart.plotLeft;
            const newY = chart.plotHeight / 2 + chart.plotTop;
            chart.renderer
              .text('La solicitud no arrojó resultados', newX, newY)
              .css({
                color: '#4572A7',
                fontSize: '24px',
              })
              .add();
          }
        });
        window.dispatchEvent(new Event('resize'));
      }
    } else {
      this.hasData = false;
    }
  }

  /**
   * Build series for chart
   */
  public buildSeries(chartOptions: IGeneralDrillData): IGeneralDrillSeries[] {
    const { drillClasification, machineDrillsByClassification } = chartOptions;
    const series = drillClasification.map((drillType) => {
      const { name, color, drillClasificationId } = drillType;
      const drillByMachine = machineDrillsByClassification.map((machine) => {
        const drillFounded = machine.machineDrillDetail.find((drillDetail) => drillDetail.drillClassificationId === drillClasificationId);
        return (drillFounded && drillFounded.totalDepth) || 0;
      });
      return {
        name,
        data: drillByMachine,
        color: `${color}80`,
        borderColor: color,
        borderWidth: 2,
        states: {
          hover: {
            color,
          },
        },
      };
    });

    return series;
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
